import { Team } from "../constants.ts";
import type { Vec3 } from "../math.ts";
import { parseGlb, transformPoint } from "./glb.ts";

export interface MapCollider {
  name: string;
  vertices: Float32Array; // world space xyz
  indices: Uint32Array;
  /** The stage (Control sub-map) it belongs to, on a map built in stages. */
  stage?: number;
}

export interface MapPoint {
  pos: Vec3;
  radius: number;
  stage: number;
}

export interface MapData {
  colliders: MapCollider[];
  /**
   * `stage`: Convoy, checkpoints the payload must have passed before this spawn is in use;
   * Control, the sub-map (Domination stage) it belongs to.
   */
  spawns: { team: Team; pos: Vec3; yaw: number; stage: number }[];
  spawnRooms: { team: Team; min: Vec3; max: Vec3; stage: number }[];
  /** Convoy: spawn-door barriers, closed while the attackers (side A) wait out Setup. */
  gates: { team: Team; min: Vec3; max: Vec3; stage: number }[];
  /** `stage`: the sub-map a pack stands in, on a map built in stages. */
  healthPacks: { id: number; large: boolean; pos: Vec3; stage?: number }[];
  /** Control: the point (on a map with stages, stage 0's). */
  point: MapPoint;
  /** Control: every stage's point, indexed by stage. Domination plays each round on one. */
  points: MapPoint[];
  /** The extent of each stage's collision, indexed by stage (maps built in stages). */
  stageBounds: { min: Vec3; max: Vec3 }[];
  /** Convoy: the payload's route. */
  track?: Track;
  bounds: { min: Vec3; max: Vec3 };
}

/** Control: the point a stage is played on (the map's only one, if it has no stages). */
export const pointAt = (map: MapData, stage: number): MapPoint => map.points[stage] ?? map.point;

/** The payload route: TRACK_* markers smoothed into a dense polyline. */
export interface Track {
  /** Dense samples along the route, about TRACK_STEP apart. */
  points: Vec3[];
  /** Unit tangent at each sample (horizontal). */
  tangents: { x: number; z: number }[];
  /** Cumulative distance (m) at each sample. */
  dist: number[];
  length: number;
  /** Checkpoints along the way (the end of the line is not one), in order. */
  checkpoints: { dist: number; bonus: number }[];
  /** Attack time before the first checkpoint, seconds. */
  startSeconds: number;
}

export interface TrackPose {
  x: number;
  y: number;
  z: number;
  /** Unit heading (horizontal). */
  fx: number;
  fz: number;
}

const TRACK_STEP = 0.5;

/** Health packs a map may carry: their availability travels as one bitmask (net/snapshot.ts). */
export const MAX_HEALTH_PACKS = 24;

/**
 * Centripetal Catmull-Rom through the markers. Only + − × ÷ and √ are used, which IEEE 754
 * rounds exactly, so every engine (server Bun, client browsers) builds a bit-identical track.
 */
export function buildTrack(markers: { pos: Vec3; checkpoint: boolean; bonus: number }[], startSeconds: number): Track {
  const P = markers.map((m) => m.pos);
  if (P.length < 2) throw new Error("track needs at least two TRACK_* markers");
  const ext = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + (a.x - b.x), y: a.y + (a.y - b.y), z: a.z + (a.z - b.z) });
  const gap = (a: Vec3, b: Vec3) => Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y) + (b.z - a.z) * (b.z - a.z));
  const knot = (a: Vec3, b: Vec3) => Math.sqrt(gap(a, b)) || 1e-6;
  const mix = (a: Vec3, b: Vec3, wa: number, wb: number): Vec3 => ({ x: a.x * wa + b.x * wb, y: a.y * wa + b.y * wb, z: a.z * wa + b.z * wb });
  const points: Vec3[] = [];
  const markerIndex: number[] = [];
  for (let i = 0; i < P.length - 1; i++) {
    const p0 = i > 0 ? P[i - 1]! : ext(P[0]!, P[1]!);
    const p1 = P[i]!;
    const p2 = P[i + 1]!;
    const p3 = i + 2 < P.length ? P[i + 2]! : ext(P[i + 1]!, P[i]!);
    const t0 = 0;
    const t1 = t0 + knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    const seg = gap(p1, p2);
    const n = Math.max(4, Math.ceil(seg / TRACK_STEP));
    markerIndex.push(points.length);
    for (let k = 0; k < n; k++) {
      const t = t1 + ((t2 - t1) * k) / n;
      const a1 = mix(p0, p1, (t1 - t) / (t1 - t0), (t - t0) / (t1 - t0));
      const a2 = mix(p1, p2, (t2 - t) / (t2 - t1), (t - t1) / (t2 - t1));
      const a3 = mix(p2, p3, (t3 - t) / (t3 - t2), (t - t2) / (t3 - t2));
      const b1 = mix(a1, a2, (t2 - t) / (t2 - t0), (t - t0) / (t2 - t0));
      const b2 = mix(a2, a3, (t3 - t) / (t3 - t1), (t - t1) / (t3 - t1));
      points.push(k === 0 ? { ...p1 } : mix(b1, b2, (t2 - t) / (t2 - t1), (t - t1) / (t2 - t1)));
    }
  }
  markerIndex.push(points.length);
  points.push({ ...P.at(-1)! });

  const dist = [0];
  for (let i = 1; i < points.length; i++) dist.push(dist[i - 1]! + gap(points[i - 1]!, points[i]!));
  const dirs: { x: number; z: number }[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1]!.x - points[i]!.x;
    const dz = points[i + 1]!.z - points[i]!.z;
    const l = Math.sqrt(dx * dx + dz * dz) || 1;
    dirs.push({ x: dx / l, z: dz / l });
  }
  const tangents = points.map((_, i) => {
    const a = dirs[Math.max(0, i - 1)]!;
    const b = dirs[Math.min(dirs.length - 1, i)]!;
    const x = a.x + b.x;
    const z = a.z + b.z;
    const l = Math.sqrt(x * x + z * z) || 1;
    return { x: x / l, z: z / l };
  });
  const checkpoints = markers.flatMap((m, i) => (m.checkpoint && i > 0 && i < markers.length - 1 ? [{ dist: dist[markerIndex[i]!]!, bonus: m.bonus }] : []));
  return { points, tangents, dist, length: dist.at(-1)!, checkpoints, startSeconds };
}

/** Position and heading `d` metres along the track (clamped to its ends). */
export function trackPose(t: Track, d: number): TrackPose {
  const D = t.dist;
  const last = D.length - 1;
  if (d <= 0) return { ...t.points[0]!, fx: t.tangents[0]!.x, fz: t.tangents[0]!.z };
  if (d >= D[last]!) return { ...t.points[last]!, fx: t.tangents[last]!.x, fz: t.tangents[last]!.z };
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (D[mid]! <= d) lo = mid;
    else hi = mid;
  }
  const span = D[hi]! - D[lo]!;
  const f = span > 0 ? (d - D[lo]!) / span : 0;
  const a = t.points[lo]!;
  const b = t.points[hi]!;
  const ta = t.tangents[lo]!;
  const tb = t.tangents[hi]!;
  const hx = ta.x + (tb.x - ta.x) * f;
  const hz = ta.z + (tb.z - ta.z) * f;
  const hl = Math.sqrt(hx * hx + hz * hz) || 1;
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f, fx: hx / hl, fz: hz / hl };
}

/** Converts a map GLB into gameplay data using the naming conventions in tools/map/mapkit.py. */
export function loadMap(glb: ArrayBuffer | Uint8Array): MapData {
  const doc = parseGlb(glb);
  const map: MapData = {
    colliders: [],
    spawns: [],
    spawnRooms: [],
    gates: [],
    healthPacks: [],
    point: { pos: { x: 0, y: 0, z: 0 }, radius: 6, stage: 0 },
    points: [],
    stageBounds: [],
    bounds: {
      min: { x: Infinity, y: Infinity, z: Infinity },
      max: { x: -Infinity, y: -Infinity, z: -Infinity },
    },
  };

  const teamOf = (name: string): Team => (/_A(_|$)/.test(name) ? Team.A : /_B(_|$)/.test(name) ? Team.B : Team.None);
  const stageOf = (extras: Record<string, unknown>) => Math.max(0, Math.round(Number(extras.stage ?? 0)) || 0);
  const staged = (extras: Record<string, unknown>) => (extras.stage === undefined ? undefined : stageOf(extras));
  const grow = (into: { min: Vec3; max: Vec3 }, b: { min: Vec3; max: Vec3 }) => {
    for (const k of ["x", "y", "z"] as const) {
      into.min[k] = Math.min(into.min[k], b.min[k]);
      into.max[k] = Math.max(into.max[k], b.max[k]);
    }
  };
  const track: { order: number; pos: Vec3; checkpoint: boolean; bonus: number; time?: number }[] = [];

  for (const node of doc.nodes) {
    const origin = transformPoint(node.world, 0, 0, 0);
    const pos = { x: origin[0], y: origin[1], z: origin[2] };
    const name = node.name;

    if (node.meshIndex !== undefined) {
      // Only gameplay meshes are read: everything else is scenery for the renderer.
      if (!(name.startsWith("SPAWNROOM_") || name.startsWith("GATE_") || name.startsWith("COL_") || node.extras.collide)) continue;
      const prims = doc.mesh(node.meshIndex);
      const verts: number[] = [];
      const idx: number[] = [];
      for (const p of prims) {
        const base = verts.length / 3;
        for (let i = 0; i < p.positions.length; i += 3) {
          const w = transformPoint(node.world, p.positions[i]!, p.positions[i + 1]!, p.positions[i + 2]!);
          verts.push(w[0], w[1], w[2]);
        }
        for (const i of p.indices) idx.push(base + i);
      }
      const vertices = Float32Array.from(verts);
      if (name.startsWith("SPAWNROOM_")) {
        map.spawnRooms.push({ team: teamOf(name), ...aabb(vertices), stage: stageOf(node.extras) });
        continue;
      }
      if (name.startsWith("GATE_")) {
        map.gates.push({ team: teamOf(name), ...aabb(vertices), stage: stageOf(node.extras) });
        continue;
      }
      if (name.startsWith("COL_") || node.extras.collide) {
        const stage = staged(node.extras);
        map.colliders.push({ name, vertices, indices: Uint32Array.from(idx), ...(stage === undefined ? {} : { stage }) });
        const b = aabb(vertices);
        grow(map.bounds, b);
        if (stage !== undefined) {
          map.stageBounds[stage] ??= { min: { x: Infinity, y: Infinity, z: Infinity }, max: { x: -Infinity, y: -Infinity, z: -Infinity } };
          grow(map.stageBounds[stage], b);
        }
      }
      continue;
    }

    if (name.startsWith("SPAWN_")) {
      map.spawns.push({ team: teamOf(name), pos, yaw: Number(node.extras.yaw ?? 0), stage: stageOf(node.extras) });
    } else if (name.startsWith("HP_")) {
      const stage = staged(node.extras);
      map.healthPacks.push({ id: map.healthPacks.length, large: name.startsWith("HP_LARGE"), pos, ...(stage === undefined ? {} : { stage }) });
    } else if (name.startsWith("POINT_")) {
      map.points.push({ pos, radius: Number(node.extras.radius ?? 6), stage: stageOf(node.extras) });
    } else if (name.startsWith("TRACK_")) {
      const e = node.extras;
      track.push({ order: Number(name.slice(6)), pos, checkpoint: !!e.checkpoint, bonus: Number(e.bonus ?? 0), time: e.time === undefined ? undefined : Number(e.time) });
    }
  }
  if (track.length) {
    track.sort((a, b) => a.order - b.order);
    map.track = buildTrack(track, track[0]!.time ?? 180);
  }
  // One point per stage, in stage order; a map without stages has its one point as stage 0.
  map.points.sort((a, b) => a.stage - b.stage);
  map.points.forEach((p, i) => {
    if (p.stage !== i) throw new Error(`POINT_* stages must run 0, 1, 2…; stage ${i} has none`);
  });
  if (map.points[0]) map.point = map.points[0];
  if (map.healthPacks.length > MAX_HEALTH_PACKS) throw new Error(`a map carries at most ${MAX_HEALTH_PACKS} health packs (the wire's mask)`);
  if (map.colliders.length === 0) throw new Error("map has no colliders");
  if (!map.spawns.some((s) => s.team === Team.A) || !map.spawns.some((s) => s.team === Team.B))
    throw new Error("map needs SPAWN_A* and SPAWN_B* markers");
  return map;
}

function aabb(v: Float32Array): { min: Vec3; max: Vec3 } {
  const min = { x: Infinity, y: Infinity, z: Infinity };
  const max = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (let i = 0; i < v.length; i += 3) {
    min.x = Math.min(min.x, v[i]!);
    min.y = Math.min(min.y, v[i + 1]!);
    min.z = Math.min(min.z, v[i + 2]!);
    max.x = Math.max(max.x, v[i]!);
    max.y = Math.max(max.y, v[i + 1]!);
    max.z = Math.max(max.z, v[i + 2]!);
  }
  return { min, max };
}

export function inAabb(p: Vec3, b: { min: Vec3; max: Vec3 }): boolean {
  return p.x >= b.min.x && p.x <= b.max.x && p.y >= b.min.y && p.y <= b.max.y && p.z >= b.min.z && p.z <= b.max.z;
}
